import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Component, ElementRef, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { USER_ACTIONS_V2 } from '../../common/endpoints';
import { ActionStatus, PagedList } from '../../model';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { HttpService } from '../../service/http.service';
import { CustomTableService } from '../../shared/custom-table';
import { ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { DatetimeFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ServitlyBadgePipe } from '../../shared/pipe/servitly-badge.pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';

@Component({
    selector: 'action-info-historical-execution-table',
    template: require('./action-info-historical-execution-table.component.html'),
    styles: [`
        custom-table ::ng-deep tr.mat-row:hover {
            cursor: default;
            background-color: inherit;
        }
    `]
})
export class ActionInfoHistoricalExectionTableComponent extends ListWidgetV2Component<ActionStatus> implements OnInit, AfterContentInit {

    @Input() actionStatus: ActionStatus;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) {
        super(appService, authenticationService, null, null, null, null, null, elementRef, coordinator);
    }
    ngOnInit(): void {
        this.pageSize = 20;
        this.fillerRowCount = 20;
        this.fillerRowHeight = 30;
        this.fillerRowHeightWithSpace = 36;
        this.fillerRowOffset = 30;
    }

    ngAfterContentInit(): void {
        this.getDisplayedColumns();
        this.getActionList();
    }

    private getDisplayedColumns(): void {
        let iconValueMapNotes = {
            "_default": {
                isFontAwesome: false,
                customIconClass: 'material-symbols-outlined filled-icon',
                customIconHtml: 'news',
                tooltipPath: 'notes',
                tooltipPosition: 'below'
            }
        }
        this.displayedColumns = [
            CustomTableService.newSimpleColumn('date', 'dateProperty', 'date'),
            CustomTableService.newSimpleColumn('executor', 'executorProperty', 'user.email'),
            CustomTableService.newPipedColumn('state', 'statusProperty', 'state', 'actionState'),
            CustomTableService.newIconColumn('notes', '', 'showNotes', iconValueMapNotes, true).withVisiblePath('showNotes').withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } })
        ];
        if (this.authenticationService.isNewStyleManagerEnabled()) {
            this.displayedColumns[2] = CustomTableService.newPipedColumn('state', 'statusProperty', 'state', ServitlyBadgePipe).withArgument({ colorTokenPrefix: 'action-execution-status' });
        }
    }

    private getActionList(): void {
        this.getPagedList(this.pageIndex, this.pageSize).then(pagedList => {
            this.updatePageListContent(pagedList.content);
            this.updateElementList(pagedList, true);
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private updatePageListContent(actionStatuses: ActionStatus[]): void {
        actionStatuses.forEach(as => {
            as['showNotes'] = !!as.notes;
            as['notes'] = this.localizationPipe.transform(as.notes);
            as['date'] = this.datetimeFormatterPipe.transform(as.timestamp, 'L');
        });
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getActionList();
    }

    private getPagedList(page: number, size: number): Promise<PagedList<ActionStatus>> {
        if (!this.actionStatus.uncoded) {
            let params = new HttpParams();
            params = params.set('page', page + '');
            params = params.set('size', size + '');
            params = params.set('language', this.authenticationService.getUser()?.language || navigator.language);
            params = params.set('actionDefinitionId', this.actionStatus.actionDefinitionId);
            params = params.set('thingId', this.actionStatus.thingId);
            params = params.set('active', false);
            return firstValueFrom(this.httpService.get<PagedList<ActionStatus>>(USER_ACTIONS_V2, params));
        }
        return Promise.resolve({ content: [], number: 0, size: 0, totalElements: 0, last: true, totalPages: 0, first: true, numberOfElements: 0 });
    }

    protected subscribeToExportServices(): void {
        /* do nothing */
    }

    protected goToDetail(element: ActionStatus): void {
        /* do nothing */
    }

}